import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { InstallDrelik } from "@/components/InstallDrelik";
import { LayoutDashboard, Store, Users, LogOut, Menu, X, FileText } from "lucide-react";

const nav = [
  { to: "/platform", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/platform/storefronts", label: "Storefronts", icon: Store },
  { to: "/platform/users", label: "Users", icon: Users },
  { to: "/platform/legal", label: "Legal Documents", icon: FileText },
];

export default function PlatformLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); navigate("/login"); };

  const Inner = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
        <Logo />
        <span className="text-xs font-semibold bg-primary/10 text-primary rounded-full px-2 py-0.5">Admin</span>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
            data-testid={`platform-nav-${n.label.toLowerCase()}`}>
            <n.icon className="h-[18px] w-[18px]" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-border flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">{user?.name?.[0]}</div>
        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground">Platform admin</p></div>
        <button onClick={doLogout} className="text-muted-foreground hover:text-destructive p-1" data-testid="platform-logout-btn"><LogOut className="h-[18px] w-[18px]" /></button>
      </div>
      <div className="px-3 pb-3"><InstallDrelik variant="ghost" className="w-full justify-start !px-2 text-muted-foreground hover:text-foreground" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-card border-r border-border flex-col z-30">{Inner}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">
            <button className="absolute top-4 right-4" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>{Inner}
          </aside>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="lg:hidden h-16 flex items-center justify-between px-4 border-b border-border bg-card sticky top-0 z-20">
          <button onClick={() => setOpen(true)}><Menu className="h-6 w-6" /></button><Logo /><div className="w-6" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
